import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Typography, Box, Card, CardContent, Grid, Paper,
  IconButton, CircularProgress, CardActions, Button, useTheme,
  useMediaQuery, Radio, RadioGroup, FormControlLabel, Alert,
  LinearProgress, Chip
} from '@mui/material';
import {
  School, Business, Info, ArrowBackIos, ArrowForwardIos,
  People, EmojiEvents, Public, CalendarToday, Poll, Article
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Cookies from 'js-cookie';

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [settings, setSettings] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [news, setNews] = useState([]);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voteSuccess, setVoteSuccess] = useState('');
  const [voteError, setVoteError] = useState('');

  useEffect(() => {
    fetchHomepageData();
    fetchNetworkNotifications();
  }, [user]);

  const fetchHomepageData = async () => {
    try {
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/homepage/settings`),
        axios.get(`${API_BASE_URL}/homepage/statistics`)
      ]);

      setSettings(settingsRes.data);
      setStatistics(statsRes.data);
    } catch (err) {
      console.error('Error fetching homepage data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNetworkNotifications = async () => {
    try {
      // Fetch news (public)
      const newsRes = await axios.get(`${API_BASE_URL}/network/news/recent`);
      setNews(newsRes.data || []);

      // Fetch polls (alumni only)
      if (user && user.role === 'Alumni') {
        const token = Cookies.get('idToken');
        const pollsRes = await axios.get(`${API_BASE_URL}/network/polls/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPolls(pollsRes.data.polls || []);
      }
    } catch (error) {
      console.error('Error fetching network notifications:', error);
    }
  };

  const handleVote = async (pollId, option) => {
    setVoteError('');
    setVoteSuccess('');

    try {
      const token = Cookies.get('idToken');
      await axios.post(`${API_BASE_URL}/network/polls/vote/${pollId}`,
        { option },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setVoteSuccess('Vote recorded!');
      fetchNetworkNotifications(); // Refresh to show updated results
      setTimeout(() => setVoteSuccess(''), 3000);
    } catch (err) {
      setVoteError(err.response?.data?.error || 'Failed to vote');
      setTimeout(() => setVoteError(''), 5000);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = { school: School, business: Business, info: Info };
    const IconComponent = icons[iconName?.toLowerCase()] || Info;
    return <IconComponent sx={{ fontSize: 40 }} />;
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? settings.carousel.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === settings.carousel.length - 1 ? 0 : prev + 1
    );
  };

  const handleCardClick = (index, card) => {
  const link = card.link || '';

  if (!link) return; // No link configured

  // Handle mailto links
  if (link.startsWith('mailto:')) {
    window.location.href = link;
    return;
  }

  // Handle external links (https://)
  if (link.startsWith('http://') || link.startsWith('https://')) {
    window.open(link, '_blank');
    return;
  }

  // Handle internal routes (like /register/alumni or /contact)
  navigate(link);
};

  if (loading || !settings) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ m: 0, p: 0 }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          mt: { xs: -2, md: -4 },
          backgroundImage: settings.hero_image
            ? `linear-gradient(rgba(16, 99, 60, 0.85), rgba(16, 99, 60, 0.85)), url(${settings.hero_image})`
            : 'linear-gradient(135deg, #10633C 0%, #0D4F2E 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          py: { xs: 6, md: 10 }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: { xs: 1, md: 2 } }}>
            <Typography
              variant="overline"
              sx={{
                color: '#FACA18',
                fontSize: { xs: '0.7rem', md: '0.85rem' },
                fontWeight: 600,
                letterSpacing: 1,
                textTransform: 'uppercase'
              }}
            >
              University of the Southern Caribbean
            </Typography>
          </Box>
          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 700,
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              textTransform: 'none'
            }}
          >
            Alumni Network
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              mb: { xs: 4, md: 5 },
              opacity: 0.95,
              maxWidth: '600px',
              fontSize: { xs: '1.1rem', md: '1.5rem' },
              textTransform: 'none'
            }}
          >
            Stay Connected. Give Back. Grow Together.
          </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {settings.action_cards.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 0,
                    transition: 'background-color 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#FACA18'
                    }
                  }}
                  onClick={() => handleCardClick(index, card)}
                >
                  <CardContent sx={{ textAlign: 'center', p: { xs: 2.5, md: 3 } }}>
                    <Box sx={{ color: '#10633C', mb: 2 }}>
                      {getIconComponent(card.icon)}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                        textTransform: 'none'
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.85rem', md: '0.95rem' },
                        textTransform: 'none'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Statistics Section */}
      {statistics && (
        <Box sx={{ bgcolor: '#F6F9F5', py: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: { xs: 3, md: 4 },
                fontSize: { xs: '1.75rem', md: '2.125rem' },
                textTransform: 'none'
              }}
            >
              Alumni by the Numbers
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: 'white',
                    border: '3px solid #10633C'
                  }}
                >
                  <People sx={{ fontSize: { xs: 36, md: 48 }, color: '#10633C', mb: 1 }} />
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: '#10633C',
                      fontSize: { xs: '1.75rem', md: '3rem' }
                    }}
                  >
                    {statistics.total_alumni}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      textTransform: 'none'
                    }}
                  >
                    Total Alumni Recorded
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: 'white',
                    border: '3px solid #FACA18'
                  }}
                >
                  <EmojiEvents sx={{ fontSize: { xs: 36, md: 48 }, color: '#FACA18', mb: 1 }} />
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: '#FACA18',
                      fontSize: { xs: '1.75rem', md: '3rem' }
                    }}
                  >
                    {statistics.total_degrees}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      textTransform: 'none'
                    }}
                  >
                    Degree Programs
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: 'white',
                    border: '3px solid #10633C'
                  }}
                >
                  <Public sx={{ fontSize: { xs: 36, md: 48 }, color: '#10633C', mb: 1 }} />
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: '#10633C',
                      fontSize: { xs: '1.75rem', md: '3rem' }
                    }}
                  >
                    {statistics.countries_represented}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      textTransform: 'none'
                    }}
                  >
                    Countries Represented
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    height: '100%',
                    bgcolor: 'white',
                    border: '3px solid #FACA18'
                  }}
                >
                  <CalendarToday sx={{ fontSize: { xs: 36, md: 48 }, color: '#FACA18', mb: 1 }} />
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: '#FACA18',
                      fontSize: { xs: '1.75rem', md: '3rem' }
                    }}
                  >
                    {statistics.latest_grad_year}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                      textTransform: 'none'
                    }}
                  >
                    Latest Graduation
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      {/* Network Notifications Section - NEWS + POLLS */}
      {(news.length > 0 || polls.length > 0) && (
        <Box sx={{ bgcolor: 'background.paper', py: { xs: 4, md: 6 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: { xs: 3, md: 4 },
                fontSize: { xs: '1.75rem', md: '2.125rem' },
                textTransform: 'none'
              }}
            >
              Network Notifications
            </Typography>

            {voteSuccess && <Alert severity="success" sx={{ mb: 2 }}>{voteSuccess}</Alert>}
            {voteError && <Alert severity="error" sx={{ mb: 2 }}>{voteError}</Alert>}

            <Grid container spacing={{ xs: 2, md: 4 }}>
              {/* News Posts */}
              {news.map((item, index) => (
                <Grid item xs={12} md={6} key={`news-${index}`}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      borderRadius: 0,
                      cursor: item.link ? 'pointer' : 'default',
                      transition: 'background-color 0.2s ease',
                      '&:hover': item.link ? { bgcolor: '#F6F9F5' } : {}
                    }}
                    onClick={() => {
                      if (item.link) {
                        window.open(item.link, '_blank');
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Article sx={{ color: '#10633C' }} />
                        <Chip label="News" size="small" color="primary" />
                      </Box>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          textTransform: 'none'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        paragraph
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textTransform: 'none'
                        }}
                      >
                        {item.content}
                      </Typography>
                      {item.date && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.date).toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>
                    {item.link && (
                      <CardActions sx={{ px: { xs: 2.5, md: 4 }, pb: { xs: 2, md: 3 } }}>
                        <Button
                          variant="text"
                          sx={{
                            color: '#10633C',
                            fontWeight: 600,
                            borderRadius: 0,
                            fontSize: { xs: '0.85rem', md: '0.95rem' }
                          }}
                        >
                          Read More →
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}

              {/* Polls - ALUMNI ONLY */}
              {user && user.role === 'Alumni' && polls.slice(0, 2).map((poll, index) => (
                <Grid item xs={12} md={6} key={`poll-${poll.id}`}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      borderRadius: 0,
                      border: '2px solid #FACA18'
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Poll sx={{ color: '#FACA18' }} />
                        <Chip label="Poll" size="small" sx={{ bgcolor: '#FACA18', color: 'white' }} />
                      </Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                          textTransform: 'none',
                          mb: 2
                        }}
                      >
                        {poll.question}
                      </Typography>

                      {poll.hasVoted ? (
                        // Show results
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                            You voted: <strong>{poll.userVote}</strong>
                          </Typography>
                          {poll.options.map((option) => {
                            const votes = poll.voteCounts?.[option] || 0;
                            const percentage = poll.totalVotes > 0
                              ? Math.round((votes / poll.totalVotes) * 100)
                              : 0;

                            return (
                              <Box key={option} sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2">{option}</Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {votes} ({percentage}%)
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={percentage}
                                  sx={{
                                    height: 8,
                                    bgcolor: '#f0f0f0',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: option === poll.userVote ? '#FACA18' : '#10633C'
                                    }
                                  }}
                                />
                              </Box>
                            );
                          })}
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Total votes: {poll.totalVotes}
                          </Typography>
                        </Box>
                      ) : (
                        // Show voting options
                        <RadioGroup>
                          {poll.options.map((option) => (
                            <Box key={option} sx={{ mb: 1 }}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => handleVote(poll.id, option)}
                                sx={{
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  borderRadius: 0,
                                  borderColor: '#10633C',
                                  color: '#10633C',
                                  '&:hover': {
                                    bgcolor: '#F6F9F5',
                                    borderColor: '#10633C'
                                  }
                                }}
                              >
                                {option}
                              </Button>
                            </Box>
                          ))}
                        </RadioGroup>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Carousel & Video Section */}
      <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Carousel */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                textTransform: 'none'
              }}
            >
              Alumni Memories
            </Typography>
            <Paper
              elevation={3}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 0
              }}
            >
              {settings.carousel.length > 0 && (
                <>
                  <Box
                    component="img"
                    src={settings.carousel[currentImageIndex].url}
                    alt={settings.carousel[currentImageIndex].title || 'Alumni photo'}
                    sx={{
                      width: '100%',
                      height: { xs: 250, md: 400 },
                      objectFit: 'cover'
                    }}
                  />
                  {settings.carousel[currentImageIndex].title && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: '#FACA18',
                        p: { xs: 1.5, md: 2 }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        {settings.carousel[currentImageIndex].title}
                      </Typography>
                    </Box>
                  )}
                  {settings.carousel.length > 1 && (
                    <>
                      <IconButton
                        onClick={handlePrevImage}
                        sx={{
                          position: 'absolute',
                          left: { xs: 5, md: 10 },
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255,255,255,0.9)',
                          borderRadius: 0,
                          '&:hover': { bgcolor: 'white' }
                        }}
                      >
                        <ArrowBackIos sx={{ ml: 0.5, fontSize: { xs: 18, md: 24 } }} />
                      </IconButton>
                      <IconButton
                        onClick={handleNextImage}
                        sx={{
                          position: 'absolute',
                          right: { xs: 5, md: 10 },
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255,255,255,0.9)',
                          borderRadius: 0,
                          '&:hover': { bgcolor: 'white' }
                        }}
                      >
                        <ArrowForwardIos sx={{ fontSize: { xs: 18, md: 24 } }} />
                      </IconButton>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          gap: 1
                        }}
                      >
                        {settings.carousel.map((_, idx) => (
                          <Box
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            sx={{
                              width: { xs: 8, md: 10 },
                              height: { xs: 8, md: 10 },
                              borderRadius: 0,
                              bgcolor: idx === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                              cursor: 'pointer',
                              transition: 'all 0.3s'
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Paper>
          </Grid>

          {/* Video & Quick Links */}
          <Grid item xs={12} md={5}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                textTransform: 'none'
              }}
            >
              Alumni Feature
            </Typography>
            <Paper
              elevation={3}
              sx={{
                overflow: 'hidden',
                borderRadius: 0,
                mb: { xs: 3, md: 4 }
              }}
            >
              <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  src={settings.video_url}
                  title="Welcome to USC"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            </Paper>

            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                textTransform: 'none'
              }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderRadius: 0,
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                    borderColor: '#10633C'
                  }
                }}
                onClick={() => window.open('https://usc.edu.tt/give', '_blank')}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, textTransform: 'none' }}>
                    Support USC
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' }, textTransform: 'none' }}
                  >
                    Make a gift to support future generations
                  </Typography>
                </CardContent>
              </Card>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderRadius: 0,
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                    borderColor: '#10633C'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, textTransform: 'none' }}>
                    Contact Us
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.85rem', md: '0.875rem' }, textTransform: 'none' }}
                  >
                    Get in touch with the alumni office
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HomePage;